/* eslint-disable camelcase */
import type { DATE_UNITS, RadioTextFilterOperation, DEVICE_TYPE } from '@hotjar/global-filters';
import { moment } from '@hotjar/webapp/lib/moment';
import type { CountryCode } from '@hotjar/country-lib';
import type { Clause } from '@hotjar/feedback/domain/Surveys/Responses/Filters/Clause';
import type { ResponseFilter } from './ResponsesFilters';
import { IncomingFeedbackFilters } from './ResponsesFilters';
import { hasFeedbackMessageFilterConverter } from '../converters/HasFeedbackMessageFilter';
import { ratingFilterConverter } from '../converters/RatingFilter';
import { pageURLFilterConverter } from '../converters/PageURLFilter';
import { dateFilterConverter } from '../converters/DateFilter';
import { hasEmailFilterConverter } from '../converters/HasEmailFilter';
import { keywordFilterConverter } from '../converters/KeywordFilter';
import { deviceFilterConverter } from '../converters/DeviceFilter';
import { countryFilterConverter } from '../converters/CountryFilter';
import type { FeedbackResponseFilters } from '../../../components/IncomingFeedback/ResponsesFilters/ResponsesFilters';

export enum IncomingFeedbackDtoFilters {
  DATE_ABSOLUTE = 'date_absolute',
  DATE_RELATIVE = 'date_relative',
  DEVICE = 'device',
  COUNTRY = 'country',
  RATING = 'emotions',
  PAGE = 'url',
  KEYWORD = 'keyword',
  FEEDBACK_MESSAGE = 'has_message',
  EMAIL = 'has_email',
}

export interface RelativeDateFilterDto {
  type: IncomingFeedbackDtoFilters.DATE_RELATIVE;
  unit: DATE_UNITS;
  value: number;
}

export interface AbsoluteDateFilterDto {
  type: IncomingFeedbackDtoFilters.DATE_ABSOLUTE;
  date_from: string;
  date_to: string;
}

export type RatingValueDto = 0 | 1 | 2 | 3 | 4;

export interface RatingFilterDto {
  type: IncomingFeedbackDtoFilters.RATING;
  values: RatingValueDto[];
}

export interface FeedbackMessageFilterDto {
  type: IncomingFeedbackDtoFilters.FEEDBACK_MESSAGE;
  value: boolean;
}

export interface EmailFilterDto {
  type: IncomingFeedbackDtoFilters.EMAIL;
  value: boolean;
}

export interface KeywordFilterDto {
  type: IncomingFeedbackDtoFilters.KEYWORD;
  operation: RadioTextFilterOperation.CONTAINS | RadioTextFilterOperation.NOT_CONTAINS;
  value: string;
}

export interface PageFilterDto {
  type: IncomingFeedbackDtoFilters.PAGE;
  operation:
    | 'EQUALS'
    | RadioTextFilterOperation.CONTAINS
    | RadioTextFilterOperation.NOT_CONTAINS
    | RadioTextFilterOperation.STARTS_WITH
    | RadioTextFilterOperation.ENDS_WITH;
  value: string;
}

export interface CountryCodeFilterDto {
  type: IncomingFeedbackDtoFilters.COUNTRY;
  values: CountryCode[];
  operation: 'EQUALS' | 'NOT_EQUALS';
}

export interface DeviceFilterDto {
  type: IncomingFeedbackDtoFilters.DEVICE;
  values: DEVICE_TYPE[];
}

export type FeedbackResponsesFilterDto =
  | RelativeDateFilterDto
  | AbsoluteDateFilterDto
  | RatingFilterDto
  | FeedbackMessageFilterDto
  | EmailFilterDto
  | KeywordFilterDto
  | PageFilterDto
  | CountryCodeFilterDto
  | DeviceFilterDto;

function mapFilterToDto(filter: ResponseFilter): FeedbackResponsesFilterDto | undefined {
  switch (filter.type) {
    case IncomingFeedbackFilters.DATE:
      return dateFilterConverter(filter.value);
    case IncomingFeedbackFilters.DEVICE:
      return deviceFilterConverter(filter.value);
    case IncomingFeedbackFilters.COUNTRY:
      return countryFilterConverter(filter.value);
    case IncomingFeedbackFilters.PAGE:
      return pageURLFilterConverter(filter.value);
    case IncomingFeedbackFilters.RATING:
      return ratingFilterConverter(filter.value);
    case IncomingFeedbackFilters.FEEDBACK_MESSAGE:
      return hasFeedbackMessageFilterConverter(filter.value);
    case IncomingFeedbackFilters.EMAIL:
      return hasEmailFilterConverter(filter.value);
    case IncomingFeedbackFilters.KEYWORD:
      return keywordFilterConverter(filter.value);
  }
}

export function mapFiltersToDto(
  filters: Partial<FeedbackResponseFilters>,
): FeedbackResponsesFilterDto[] {
  return Object.values(filters)
    .filter((v): v is ResponseFilter => v !== undefined)
    .map(mapFilterToDto)
    .filter((filter): filter is FeedbackResponsesFilterDto => filter !== undefined);
}

// A helper function to calculate how far back in time the `recordings/feedback-responses` API
// should look for recordings based on selected date filter
export function mapFiltersToDaysAgo(filters: FeedbackResponsesFilterDto[], created: Date): number {
  // find date filter
  const dateFilter = filters.find(
    (filter) =>
      filter.type === IncomingFeedbackDtoFilters.DATE_ABSOLUTE ||
      filter.type === IncomingFeedbackDtoFilters.DATE_RELATIVE,
  ) as RelativeDateFilterDto | AbsoluteDateFilterDto | undefined;

  // calulate `days ago` value based on the selected range
  if (dateFilter) {
    if (dateFilter.type === IncomingFeedbackDtoFilters.DATE_RELATIVE) {
      return moment().diff(moment().subtract(dateFilter.value, dateFilter.unit), 'days');
    }
    return moment().diff(dateFilter.date_from, 'days');
  }

  // if the date filter is not specified return the number of days since the poll/IF was created
  return moment().diff(created, 'days') + 1;
}

// A helper function to calculate how far back in time the `recordings/feedback-responses` API
// should look for recordings based on selected date filter
export function mapFilterDataToDaysAgo(filters: Clause[], created: Date): number {
  // find date filter
  const dateFilter = filters.find(
    (filter) => filter.type === 'date_absolute' || filter.type === 'date_relative',
  );

  // calulate `days ago` value based on the selected range
  if (dateFilter) {
    if (dateFilter.type === 'date_relative') {
      return moment().diff(moment().subtract(dateFilter.value, dateFilter.unit), 'days');
    }
    if (dateFilter.type === 'date_absolute') {
      return moment().diff(dateFilter.date_from, 'days');
    }
  }

  // if the date filter is not specified return the number of days since the poll/IF was created
  return moment().diff(created, 'days') + 1;
}
